import { ComponentChildren, ComponentProps } from 'preact';
import { DropdownArrow } from '../PRIVATE_SelectCommon';
import { Flex } from '../UNSAFE_Flex';
import { TextField } from '../UNSAFE_TextField';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { SelectedValuesCount } from './SelectedValuesCount';
import { IconButton } from '../UNSAFE_IconButton';
import { NavLeft } from '../UNSAFE_RedwoodIcons/NavLeft';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { CancelS } from '../UNSAFE_RedwoodIcons/CancelS';
import { LayoutColumnSpan } from '../utils/UNSAFE_styles/Layout';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';

type TextFieldProps = ComponentProps<typeof TextField>;
type PassThroughTextFieldProps = TestIdProps &
  Pick<
    TextFieldProps,
    | 'compactUserAssistance'
    | 'contentVariant'
    | 'id'
    | 'inlineUserAssistance'
    | 'label'
    | 'labelEdge'
    | 'labelStartWidth'
    | 'mainFieldRef'
    | 'onBlur'
    | 'onFocus'
    | 'onMouseDown'
    | 'rootRef'
    | 'statusVariant'
    | 'styleVariant'
  >;

type DropdownArrowProps = ComponentProps<typeof DropdownArrow>;
type IconButtonProps = ComponentProps<typeof IconButton>;
type SelectedValuesCountProps = ComponentProps<typeof SelectedValuesCount>;

type Props = PassThroughTextFieldProps & {
  children?: ComponentChildren;
  columnSpan?: LayoutColumnSpan;
  fieldLabel?: string;
  hasInsideLabel?: boolean;
  isBackButtonShown?: boolean;
  isClearButtonShown?: boolean;
  isDropdownArrowShown?: boolean;
  isDropdownSelectedOnlyView?: boolean;
  isSelectedValuesCountShown?: boolean;
  selectedValuesCount?: number;
  onBackButtonClick?: IconButtonProps['onAction'];
  onClearButtonClick?: IconButtonProps['onAction'];
  onDropdownArrowClick?: DropdownArrowProps['onClick'];
  onSelectedValuesCountKeyDown?: SelectedValuesCountProps['onKeyDown'];
  onSelectedValuesCountKeyUp?: SelectedValuesCountProps['onKeyUp'];
  onSelectedValuesCountMouseDown?: SelectedValuesCountProps['onMouseDown'];
  onSelectedValuesCountToggle?: SelectedValuesCountProps['onToggle'];
};

export function SelectMultipleField({
  children,
  columnSpan,
  fieldLabel,
  hasInsideLabel,
  isBackButtonShown,
  isClearButtonShown,
  isDropdownArrowShown,
  isDropdownSelectedOnlyView,
  isSelectedValuesCountShown,
  selectedValuesCount,
  onBackButtonClick,
  onClearButtonClick,
  onDropdownArrowClick,
  onSelectedValuesCountKeyDown,
  onSelectedValuesCountKeyUp,
  onSelectedValuesCountMouseDown,
  onSelectedValuesCountToggle,
  ...passThroughTextFieldProps
}: Props) {
  const { isDisabled, isLoading } = useFormFieldContext();
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const backButton = isBackButtonShown ? (
    <IconButton
      aria-label={translations.selectMultiple_back()}
      size="xs"
      variant="ghost"
      onAction={onBackButtonClick}>
      <NavLeft />
    </IconButton>
  ) : null;

  const clearButton = isClearButtonShown ? (
    <IconButton
      aria-label={translations.formControl_clear()}
      size="sm"
      variant="ghost"
      onAction={onClearButtonClick}>
      <CancelS />
    </IconButton>
  ) : null;

  const dropdownArrow = isDropdownArrowShown ? (
    <DropdownArrow
      isDisabled={isDisabled}
      onClick={onDropdownArrowClick}
      testId={`${passThroughTextFieldProps.testId}_dropdownarrow`}
    />
  ) : null;

  const endContent = isLoading ? undefined : !isSelectedValuesCountShown ? (
    <>
      {isClearButtonShown && clearButton}
      {isDropdownArrowShown && dropdownArrow}
    </>
  ) : (
    <Flex align="center" justify="center" gap={[0, '1x']}>
      <SelectedValuesCount
        aria-label={fieldLabel}
        count={selectedValuesCount}
        isDisabled={isDisabled}
        isSelected={isDropdownSelectedOnlyView}
        onKeyDown={onSelectedValuesCountKeyDown}
        onKeyUp={onSelectedValuesCountKeyUp}
        onMouseDown={onSelectedValuesCountMouseDown}
        onToggle={onSelectedValuesCountToggle}
      />
      {isClearButtonShown && clearButton}
      {isDropdownArrowShown && dropdownArrow}
    </Flex>
  );

  return (
    <TextField
      columnSpan={columnSpan}
      startContent={isBackButtonShown && backButton}
      mainContent={children}
      endContent={endContent}
      {...passThroughTextFieldProps}
    />
  );
}
